import React, { useState, useRef, useEffect } from 'react';
import { MessageSquareText } from 'lucide-react';
import { ChatHeader } from './ChatHeader';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { ReportModal } from './ReportModal';
import {
  buildAssistantMessageId,
  getChatHistory,
  getChatReactions,
  linkReactionRecordsToMessages,
  reactionToFeedback,
  sendChatMessage,
  sendChatReaction,
  truncateMessageContent,
} from '../../services/chatbot/chatApi';
import type { ChatContentBlock, ChatSource } from './ChatSchemas';
import { getCookie } from '../../shared/utils';
import '../../ChatbotStyles/ChatWidget.scss';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  timeString?: string;
  isReport?: boolean;
  fileLinks?: string[];
  contentBlocks?: ChatContentBlock[];
  sources?: ChatSource[];
  feedback?: {
    liked: boolean;
    disliked: boolean;
    favorited: boolean;
  };
}

const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please refresh the page to continue.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const isAuthError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.toLowerCase().includes('authentication failed');

export interface ChatWidgetProps {
  initialAccessToken?: string;
  initialCustomerId?: string;
  initialRole: string;
  initialCategory?: string;
}

export const ChatWidget = ({
  initialAccessToken = '',
  initialCustomerId = '',
  initialRole,
  initialCategory = '',
}: ChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        "Hello 👋 I'm Ōmcare Assistant. You can ask me about patient adherence, facility metrics, or technical performance — I'll fetch insights instantly.",
      timestamp: new Date(),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [currentInput, setCurrentInput] = useState('');
  const [messageCount, setMessageCount] = useState(1);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const [customerId] = useState<string>(initialCustomerId);
  const [role] = useState<string>(initialRole);
  const showCitations = role === 'CUSTOMER_CARE';
  const [token] = useState<string>(initialAccessToken);
  const [category] = useState<string>(initialCategory);
  const [apiError, setApiError] = useState<string>('');
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [currentReportContent, setCurrentReportContent] = useState<string>('');
  const [currentReportTitle, setCurrentReportTitle] = useState<string>('');
  const [currentReportFileLinks, setCurrentReportFileLinks] = useState<
    string[]
  >([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const getActiveToken = () => getCookie('access_token') || token;

  const scrollToBottom = () => {
    try {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Error scrolling to bottom:', error);
      try {
        messagesEndRef.current?.scrollIntoView();
      } catch (fallbackError) {
        console.error('Fallback scroll failed:', fallbackError);
      }
    }
  };

  const handleMinimize = () => {
    try {
      if (!isMinimized && messagesContainerRef.current) {
        setSavedScrollPosition(messagesContainerRef.current.scrollTop);
      }
      setIsMinimized(!isMinimized);
    } catch (error) {
      console.error('Error minimizing chat:', error);
      try {
        setIsMinimized(!isMinimized);
      } catch (fallbackError) {
        console.error('Critical: Failed to minimize:', fallbackError);
      }
    }
  };

  useEffect(() => {
    try {
      if (
        !isMinimized &&
        messagesContainerRef.current &&
        savedScrollPosition > 0
      ) {
        messagesContainerRef.current.scrollTop = savedScrollPosition;
      }
    } catch (error) {
      console.error('Error restoring scroll position:', error);
    }
  }, [isMinimized, savedScrollPosition]);

  useEffect(() => {
    try {
      scrollToBottom();
    } catch (error) {
      console.error('Error in scroll effect:', error);
    }
  }, [messageCount, isTyping]);

  useEffect(() => {
    try {
      if (!isTyping) {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Error in typing status effect:', error);
      try {
        setIsTyping(false);
        setStatusMessage('');
      } catch {}
    }
  }, [isTyping]);

  useEffect(() => {
    const loadChatHistory = async () => {
      if (
        !customerId.trim() ||
        historyLoaded ||
        !isOpen ||
        !shouldLoadHistory
      ) {
        return;
      }

      const customerIdNum = Number(customerId);

      if (isNaN(customerIdNum) || customerIdNum <= 0) {
        return;
      }

      try {
        const activeToken = getCookie('access_token') || token;
        const [historyData, reactions] = await Promise.all([
          getChatHistory(customerIdNum, activeToken || undefined),
          getChatReactions(customerIdNum, activeToken || undefined).catch(
            () => [],
          ),
        ]);

        if (historyData && historyData.length > 0) {
          const reactionsByContent = new Map(
            reactions.map((reaction) => [
              truncateMessageContent(reaction.message),
              reaction,
            ]),
          );

          const historyMessages: Message[] = historyData.map((msg, index) => {
            const reaction =
              msg.role === 'assistant'
                ? reactionsByContent.get(truncateMessageContent(msg.content))
                : undefined;

            return {
              id:
                msg.role === 'assistant'
                  ? buildAssistantMessageId(msg.content, customerIdNum)
                  : `history-user-${index}`,
              role: msg.role,
              content: msg.content,
              timestamp: new Date(),
              timeString: msg.time,
              isReport: msg.report === true,
              fileLinks:
                msg.fileLink && msg.fileLink.length > 0
                  ? msg.fileLink
                  : undefined,
              contentBlocks:
                msg.contentBlocks && msg.contentBlocks.length > 0
                  ? msg.contentBlocks
                  : undefined,
              sources:
                msg.sources && msg.sources.length > 0
                  ? msg.sources
                  : undefined,
              feedback: reaction
                ? reactionToFeedback(reaction.reaction, reaction.favourite)
                : undefined,
            };
          });

          linkReactionRecordsToMessages(historyMessages, reactions);

          setMessages((prev) => {
            const welcomeMsg = prev.find((m) => m.id === '1');
            const newMessages = [
              ...historyMessages,
              ...(welcomeMsg ? [welcomeMsg] : []),
            ];
            return newMessages;
          });
          setMessageCount(1 + historyMessages.length);
        }

        setHistoryLoaded(true);
        setShouldLoadHistory(false);
      } catch (error) {
        console.error('Error loading chat history:', error);
        setHistoryLoaded(true);
        setShouldLoadHistory(false);
      }
    };

    loadChatHistory();
  }, [customerId, token, historyLoaded, isOpen, shouldLoadHistory]);

  const handleSendMessage = async (content: string) => {
    setApiError('');

    if (!getActiveToken()) {
      setApiError(SESSION_EXPIRED_MESSAGE);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessageCount((prev) => prev + 1);
    setIsTyping(true);
    setStatusMessage('Thinking...');

    try {
      const response = await sendChatMessage(
        {
          query: content,
          customerId: Number(customerId),
          role: role,
          token: getActiveToken(),
        },
        (progressMessage: string) => {
          setStatusMessage(progressMessage);
        },
      );

      const aiMessage: Message = {
        id: buildAssistantMessageId(response.response, Number(customerId)),
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        isReport: response.report,
        fileLinks:
          response.fileLink && response.fileLink.length > 0
            ? response.fileLink
            : undefined,
        contentBlocks:
          response.contentBlocks && response.contentBlocks.length > 0
            ? response.contentBlocks
            : undefined,
        sources:
          response.sources && response.sources.length > 0
            ? response.sources
            : undefined,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setMessageCount((prev) => prev + 1);
    } catch (error) {
      console.error('API Error:', error);

      setApiError(
        isAuthError(error) ? SESSION_EXPIRED_MESSAGE : GENERIC_ERROR_MESSAGE,
      );

      setMessages((prev) => prev.slice(0, -1));
      setMessageCount((prev) => prev - 1);
    } finally {
      setIsTyping(false);
    }
  };

  const handleClose = () => {
    try {
      setIsOpen(false);
      setIsMinimized(false);
      setCurrentInput('');
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content:
            "Hello 👋 I'm Ōmcare Assistant. You can ask me about patient adherence, facility metrics, or technical performance — I'll fetch insights instantly.",
          timestamp: new Date(),
        },
      ]);
      setMessageCount(1);
      setIsTyping(false);
      setApiError('');
      setHistoryLoaded(false);
      setShouldLoadHistory(false);
    } catch (error) {
      console.error('Error closing chat:', error);
      try {
        setIsOpen(false);
        setIsTyping(false);
        setHistoryLoaded(false);
        setShouldLoadHistory(false);
      } catch (fallbackError) {
        console.error('Critical: Failed to close chat:', fallbackError);
      }
    }
  };

  const handleViewReport = (reportContent: string, fileLinks?: string[]) => {
    setCurrentReportContent(reportContent);
    setCurrentReportTitle('Report');
    setCurrentReportFileLinks(fileLinks || []);
    setIsReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setCurrentReportContent('');
    setCurrentReportTitle('');
    setCurrentReportFileLinks([]);
  };

  const handleFeedback = async (
    messageId: string,
    feedbackType: 'liked' | 'disliked' | 'favorited',
    value: boolean,
  ) => {
    try {
      if (!messageId || typeof messageId !== 'string') {
        console.error('Invalid messageId:', messageId);
        return;
      }

      if (!['liked', 'disliked', 'favorited'].includes(feedbackType)) {
        console.error('Invalid feedbackType:', feedbackType);
        return;
      }

      if (!customerId.trim()) {
        setApiError('Something went wrong. Please try again.');
        return;
      }

      const message = messages.find((m) => m.id === messageId);
      if (!message) {
        console.error('Message not found:', messageId);
        return;
      }

      const previousMessages = [...messages];
      const currentFeedback = message.feedback || {
        liked: false,
        disliked: false,
        favorited: false,
      };

      let newFeedback = { ...currentFeedback };
      if (feedbackType === 'liked') {
        newFeedback.liked = value;
        if (value) newFeedback.disliked = false;
      } else if (feedbackType === 'disliked') {
        newFeedback.disliked = value;
        if (value) newFeedback.liked = false;
      } else if (feedbackType === 'favorited') {
        newFeedback.favorited = value;
        if (value && !newFeedback.liked && !newFeedback.disliked) {
          newFeedback.liked = true;
        }
      }

      const hasAnyFeedback =
        newFeedback.liked || newFeedback.disliked || newFeedback.favorited;
      if (!hasAnyFeedback) {
        setMessages(previousMessages);
        return;
      }

      const reactionValue: 'liked' | 'disliked' = newFeedback.liked
        ? 'liked'
        : 'disliked';

      setMessages(
        messages.map((m) =>
          m.id === messageId ? { ...m, feedback: newFeedback } : m,
        ),
      );

      try {
        await sendChatReaction(
          message,
          reactionValue,
          newFeedback.favorited,
          customerId,
          category,
          getActiveToken(),
        );

        setApiError('');
      } catch (error) {
        console.error('Reaction API Error:', error);
        setMessages(previousMessages);
        setApiError('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleFeedback:', error);
      setApiError('Something went wrong. Please try again.');
    }
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    if (!historyLoaded) {
      setShouldLoadHistory(true);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpenChat}
        className="chat-widget-button"
        aria-label="Open chat"
      >
        <MessageSquareText size={24} />
      </button>
    );
  }

  return (
    <>
      <div
        className={`chat-widget-container ${isMinimized ? 'minimized' : ''}`}
      >
        <ChatHeader
          onMinimize={handleMinimize}
          onClose={handleClose}
          isMinimized={isMinimized}
        />

        {!isMinimized && (
          <>
            <div className="chat-messages-container" ref={messagesContainerRef}>
              <div className="messages-list">
                {messages.map((message, index) => (
                  <ChatMessage
                    key={`${message.id}-${index}`}
                    message={message}
                    showCitations={showCitations}
                    onFeedback={(feedbackType, value) =>
                      handleFeedback(message.id, feedbackType, value)
                    }
                    onViewReport={handleViewReport}
                  />
                ))}
                {isTyping && <TypingIndicator statusMessage={statusMessage} />}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="chat-input-section">
              {apiError && <div className="api-error">{apiError}</div>}

              <div className="input-wrapper">
                <ChatInput
                  value={currentInput}
                  onChange={setCurrentInput}
                  onSendMessage={handleSendMessage}
                  customerId={customerId}
                  role={role}
                  token={token}
                  isLoading={isTyping}
                  historyLoaded={historyLoaded}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        reportContent={currentReportContent}
        reportTitle={currentReportTitle}
        fileLinks={currentReportFileLinks}
      />
    </>
  );
};
